import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, btn, inp } from './adminHelpers';

function PaymentRow({ p }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr 
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ 
        borderBottom: '1px solid rgba(255,255,255,0.04)', 
        transition: 'background 0.2s', 
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent' 
      }}
    >
      {/* Stripe ID */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#9F5CFF', background: 'rgba(159,92,255,0.08)', border: '1px solid rgba(159,92,255,0.15)', padding: '4px 8px', borderRadius: 6 }}>
          {p.stripe_payment_intent_id}
        </span>
      </td>

      {/* Customer Info */}
      <td style={{ padding: '16px 18px' }}>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{p.customer?.name || 'Unknown'}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>{p.customer?.email}</div>
      </td>

      {/* Booking details */}
      <td style={{ padding: '16px 18px' }}>
        <div style={{ fontSize: 13, color: '#fff' }}>Booking #{p.booking_id}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>
          {p.Booking ? `${p.Booking.booth_type} (${(() => { const [y,m,d] = p.Booking.event_date.split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString(); })()})` : '—'}
        </div>
      </td>

      {/* Amount */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{ fontSize: 14, color: '#27C85A', fontWeight: 700 }}>
          ${parseFloat(p.amount).toFixed(2)}
        </span>
      </td>

      {/* Created At */}
      <td style={{ padding: '16px 18px', fontSize: 12, color: '#e0e0e0' }}>
        {new Date(p.created_at).toLocaleString()}
      </td>

      {/* Status badge */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{ display: 'inline-block', background: 'rgba(39,200,90,0.1)', border: '1px solid rgba(39,200,90,0.3)', color: '#27C85A', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 50, textTransform: 'uppercase' }}>
          {p.status}
        </span>
      </td>
    </tr>
  );
}

export default function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await ax().get(`${API}/api/admin/payments`, { params });
      setPayments(res.data.payments || []);
      setTotalPages(res.data.pages || 1);
      setTotalPayments(res.data.total || 0);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load payments history.');
    } finally {
      setLoading(false);
    }
  }, [page, search, startDate, endDate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return (
    <div style={{ padding: '4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: '#fff', fontWeight: 700 }}>💳 Stripe Payment History</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#a0a0b8' }}>
            View and audit all deposit charges processed through the Stripe gateway.
          </p>
        </div>
        <div style={{ background: 'rgba(39,200,90,0.1)', border: '1px solid rgba(39,200,90,0.2)', padding: '10px 16px', borderRadius: 12 }}>
          <span style={{ fontSize: 11, color: '#27C85A', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, display: 'block' }}>Total Transactions</span>
          <span style={{ fontSize: 18, color: '#fff', fontWeight: 800 }}>{totalPayments} verified</span>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Search Customer</label>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by customer name or email…"
            style={inp(false)} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Start Date</label>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
            style={inp(false)} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>End Date</label>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
            style={inp(false)} />
        </div>
        {(search || startDate || endDate) && (
          <button onClick={() => { setSearch(''); setStartDate(''); setEndDate(''); setPage(1); }}
            style={{ ...btn('#555', true), padding: '10px 16px', borderRadius: 10, alignSelf: 'flex-end', height: 40 }}>
            Reset
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 14, background: 'rgba(227,67,100,0.1)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 12, marginBottom: 20, color: '#E34364', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#a0a0b8', fontSize: 14 }}>Loading payment records…</div>
      ) : payments.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 40, margin: '0 0 12px' }}>💸</p>
          <p style={{ color: '#a0a0b8', fontSize: 14, fontWeight: 600, margin: 0 }}>No payments processed yet.</p>
          <p style={{ color: '#555', fontSize: 12, margin: '6px 0 0' }}>Stripe payments will appear here once customers confirm deposits.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table Wrapper */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 800 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                    {['Stripe Payment Intent ID', 'Customer Info', 'Booking Details', 'Amount Paid', 'Date & Time', 'Status'].map(h => (
                      <th key={h} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <PaymentRow key={p.id} p={p} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, padding: '0 4px' }}>
              <span style={{ fontSize: 12, color: '#a0a0b8' }}>Page {page} of {totalPages}</span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: page === 1 ? '#222' : '#9F5CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: page === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                  ← Previous
                </button>
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ background: page === totalPages ? '#222' : '#9F5CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: page === totalPages ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
